import cn from 'classnames'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { RowsControl } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/styledComponents/rowsControl/RowsControl'
import { useCallback, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Input, Textarea } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'

import { TRANSLATION_NAMESPACE } from '../../configs'

import { tooltipMessage } from './configs'

import styles from './MultiLinesControl.module.scss'

export interface IMultiLinesItem {
  value: string
  placeholder?: string
  isRequired?: boolean
}

interface IProps {
  items: IMultiLinesItem[]
  onChange: (items: IMultiLinesItem[]) => void
  maxLen?: number
  minLen?: number
  showBottomControl?: boolean
  showRowControl?: boolean
  isTextArea?: boolean
  withDot?: boolean
}

export const MultiLinesControl = ({
  items,
  onChange,
  maxLen = 5,
  minLen,
  showBottomControl = true,
  showRowControl = false,
  isTextArea,
  withDot,
}: IProps) => {
  const handleDotChange = useCallback(
    (items: IMultiLinesItem[]) => {
      if (withDot) {
        const fixedItems = items.map((el) => {
          if ((el.value ?? '').startsWith('•')) {
            return el
          }

          return { ...el, value: `• ${el.value}` }
        })
        onChange(fixedItems)

        return
      }
      onChange(items)
    },
    [onChange, withDot]
  )

  const handleChange = useCallback(
    (index: number) => (value: string) => {
      const newItems = [...items]
      newItems[index] = { ...items[index], value }
      handleDotChange(newItems)
    },
    [items, handleDotChange]
  )

  const addRow = useCallback(() => {
    handleDotChange([...items, { value: '' }])
  }, [handleDotChange, items])

  const deleteRow = useCallback(
    (index: number) => {
      const clonedValues = structuredClone(items)
      clonedValues.splice(index, 1)
      if (!clonedValues.length) {
        clonedValues.push({ value: '' })
      }
      handleDotChange(clonedValues)
    },
    [items, handleDotChange]
  )

  const deleteLastRow = useCallback(() => {
    handleDotChange(items.slice(0, -1))
  }, [handleDotChange, items])

  const canAddNewRow = useMemo(() => {
    const emptyRows = items.filter((el) => !el.value)

    return emptyRows.length === 0
  }, [items])

  const tooltipRender = useCallback(() => {
    return <>{tooltipMessage}</>
  }, [])

  return (
    <div className={styles.root}>
      {items.map((item, index) => {
        const showAddNewRow = item.value.length === index + 1
        const label = `${item.placeholder || ''}${item.value ? '-short' : ''}`
        const isShowValueWarning = item.isRequired && !item.value

        if (isTextArea) {
          return (
            <div className={styles.inlineControls} key={index}>
              <Textarea
                classes={{ root: styles.input }}
                value={item.value}
                onChange={handleChange(index)}
                placeholder={item.placeholder}
                rows={item.value.split('\n').length}
              />
              <RenderWhen condition={showRowControl}>
                <RowsControl
                  containerClassName={styles.column}
                  addRow={addRow}
                  deleteRow={() => deleteRow(index)}
                  maxLen={maxLen}
                  minLen={minLen}
                  items={items.map((el) => el.value)}
                  canAddNewRow={canAddNewRow}
                  showAddNewRow={showAddNewRow}
                />
              </RenderWhen>
            </div>
          )
        }

        return (
          <Tooltip
            key={`${TRANSLATION_NAMESPACE}${index}-tooltip`}
            disabled={!isShowValueWarning}
            tipContent={tooltipRender}
          >
            <Input
              key={`${TRANSLATION_NAMESPACE}${index}-input`}
              value={item.value}
              onChange={handleChange(index)}
              label={<Translation text={label} span />}
              classes={{ root: cn(styles.input, { [styles.warning]: isShowValueWarning }) }}
            />
          </Tooltip>
        )
      })}

      <RenderWhen condition={showBottomControl}>
        <RowsControl
          addRow={addRow}
          deleteRow={deleteLastRow}
          maxLen={maxLen}
          minLen={minLen}
          items={items.map((el) => el.value)}
          canAddNewRow={canAddNewRow}
        />
      </RenderWhen>
    </div>
  )
}
